import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import CurrencyIcon from '@/components/ui/icons/currency-icon';
import useSearchParams from '@/hooks/useSearchParams';
import useSearchUrlParams from '@/hooks/algolia/useSearchUrlParams';
import { PRICE_FILTER_KEY } from '@/constants';
import { IoChevronDown } from 'react-icons/io5';
import { event_filters_used } from '@/utils/gtm';

const MIN_RANGE = 0;
const MAX_RANGE = 5000;

export default function PriceFilter({ mode = 'category', searchIndexName }) {
  const locale = useLocale();
  const t = useTranslations('common');
  const { setFilterQueryHandler, getFilterObj } = useSearchParams();
  const { setFilters } = useSearchUrlParams(searchIndexName);

  const [open, setOpen] = useState(false);

  const currentFilters = useMemo(
    () => getFilterObj?.filters?.split(',') || [],
    [getFilterObj.filters]
  );

  const getPriceFromSearchParams = useMemo(() => {
    if (mode === 'algolia') {
      const priceFilter = currentFilters.find((filter) =>
        filter.startsWith(PRICE_FILTER_KEY)
      );
      if (priceFilter) {
        const arr = priceFilter.split(' ');
        return {
          minPrice: Number(arr[2]),
          maxPrice: Number(arr[arr.length - 1]),
        };
      }
      return { minPrice: MIN_RANGE, maxPrice: MAX_RANGE };
    } else {
      return {
        minPrice: Number(getFilterObj?.minPrice || MIN_RANGE),
        maxPrice: Number(getFilterObj?.maxPrice || MAX_RANGE),
      };
    }
  }, [currentFilters, getFilterObj, mode]);

  const [minPrice, setMinPrice] = useState(getPriceFromSearchParams.minPrice);
  const [maxPrice, setMaxPrice] = useState(getPriceFromSearchParams.maxPrice);

  useEffect(() => {
    setMinPrice(getPriceFromSearchParams.minPrice);
    setMaxPrice(getPriceFromSearchParams.maxPrice);
  }, [getPriceFromSearchParams.minPrice, getPriceFromSearchParams.maxPrice]);

  const didMountRef = useRef(false);
  const userChangedRef = useRef(false);

  const applyPriceFilter = useCallback(
    (min, max) => {
      if (mode === 'algolia') {
        const priceFilterValue = `${PRICE_FILTER_KEY} >= ${min} AND ${PRICE_FILTER_KEY} <= ${max}`;
        const updatedFilters = [
          ...currentFilters.filter((f) => !f.startsWith(PRICE_FILTER_KEY)),
          priceFilterValue,
        ];
        setFilters(updatedFilters);
      } else {
        setFilterQueryHandler({
          minPrice: min,
          maxPrice: max,
          p: null,
        });
      }
    },
    [mode, setFilterQueryHandler, setFilters, currentFilters]
  );

  const handleRangeChange = useCallback((value) => {
    userChangedRef.current = true;
    setMinPrice(value[0]);
    setMaxPrice(value[1]);
  }, []);

  const handleRangeChangeComplete = useCallback(
    (value) => {
      applyPriceFilter(value[0], value[1]);
    },
    [applyPriceFilter]
  );

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    const timeout = setTimeout(() => {
      if (minPrice > maxPrice) return;
      if (userChangedRef.current) {
        event_filters_used({
          filter_name: 'Price',
          filter_value: `${minPrice}-${maxPrice}`,
          filter_value_id: `price_${minPrice}_${maxPrice}`,
          filter_action: 'apply',
        });
        userChangedRef.current = false;
      }
      applyPriceFilter(minPrice, maxPrice);
    }, 500);

    return () => clearTimeout(timeout);
  }, [minPrice, maxPrice]);

  return (
    <section className='border-border-50 rounded-2xl border bg-white p-4'>
      {/* HEADER — CLICK TO TOGGLE */}
      <header
        className={`flex cursor-pointer items-center justify-between`}
        onClick={() => setOpen((o) => !o)}
      >
        <h2 className='font-bold'>{t('Price')}</h2>

        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-500'>
            {minPrice || MIN_RANGE}{' '}
            <CurrencyIcon className='mx-1 inline size-4' /> —{' '}
            {maxPrice || MAX_RANGE}{' '}
            <CurrencyIcon className='mx-1 inline size-4' />
          </span>

          <IoChevronDown
            className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      </header>

      {/* COLLAPSIBLE CONTENT */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'mt-3 max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {/* Slider */}
        <div className='mb-4'>
          <div className='relative mx-auto w-[93%]'>
            <Slider
              range
              min={MIN_RANGE}
              max={MAX_RANGE}
              reverse={locale === 'ar'}
              step={20}
              value={[minPrice, maxPrice]}
              allowCross={false}
              onChange={handleRangeChange}
              onChangeComplete={handleRangeChangeComplete}
              styles={{
                track: { backgroundColor: 'var(--color-primary)', height: 6 },
                handle: {
                  backgroundColor: 'var(--color-primary)',
                  border: 'none',
                  opacity: 1,
                  width: 16,
                  height: 16,
                },
                rail: { backgroundColor: '#f3f4f6', height: 6 },
              }}
            />
          </div>
        </div>

        {/* Inputs */}
        <div className='grid grid-cols-2 gap-3'>
          <label className='text-sm'>
            <div className='mb-1 font-medium'>{t('min_limit')}</div>
            <input
              type='number'
              min={MIN_RANGE}
              max={MAX_RANGE}
              value={minPrice}
              onChange={(e) => {
                userChangedRef.current = true;
                setMinPrice(Number(e.target.value));
              }}
              className='border-border-50 w-full rounded-xl border px-3 py-2'
            />
          </label>

          <label className='text-sm'>
            <div className='mb-1 font-medium'>{t('max_limit')}</div>
            <input
              type='number'
              min={MIN_RANGE}
              max={MAX_RANGE}
              value={maxPrice}
              onChange={(e) => {
                userChangedRef.current = true;
                setMaxPrice(Number(e.target.value));
              }}
              className='border-border-50 w-full rounded-xl border px-3 py-2'
            />
          </label>
        </div>

        {/* Error */}
        {+minPrice > +maxPrice && (
          <p className='mt-2 text-xs text-red-500'>
            {t('price_filter_error_key')}
          </p>
        )}
      </div>
    </section>
  );
}
